/**
 * AwarenessStrip — composite awareness score across three dimensions:
 *   • Endpoint coverage    — tools / discovered endpoints
 *   • Module coverage      — modules with >= partial coverage
 *   • Entity awareness     — Zod schemas the agent can reason about
 * Plus a freshness signal — how long since the scanner last ran.
 *
 * Extracted from AiUsageDashboard.tsx:511-625. The arithmetic that used to
 * live inline here now lives in coverageScore.ts, where it is unit-tested;
 * this file only renders what those functions return.
 */
import React from 'react';
import type { ToolCatalog, ToolCatalogModule } from '../../../services/aiControlApi';
import {
  awarenessColor, awarenessScore, endpointPct as computeEndpointPct,
  formatRelative, freshnessPct as computeFreshnessPct, modulePct as computeModulePct,
} from './coverageScore';

interface Props {
  inventory: NonNullable<ToolCatalog['inventory']>;
  coverageTotals: ToolCatalog['totals'];
  modules: ToolCatalogModule[];
}

const ScoreCell: React.FC<{ label: string; value: number; sub: string }> = ({ label, value, sub }) => (
  <div>
    <div className={`text-2xl font-bold ${awarenessColor(value)}`}>{value}%</div>
    <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-[10px] text-gray-400 truncate">{sub}</div>
  </div>
);

const StatCell: React.FC<{ label: string; value: number; accent?: 'rose' }> = ({ label, value, accent }) => (
  <div>
    <div className={`text-2xl font-bold ${accent === 'rose' ? 'text-rose-600' : 'text-gray-900'}`}>
      {value.toLocaleString()}
    </div>
    <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
  </div>
);

const AwarenessStrip: React.FC<Props> = ({ inventory, coverageTotals, modules }) => {
  const covered = inventory.totalEndpoints - inventory.totalGaps;
  const endpointPct = computeEndpointPct(inventory.totalEndpoints, inventory.totalGaps);

  const mods = computeModulePct(modules);

  // Entity awareness — entities that >=1 tool references via heuristic path
  // match. "Covered" is a loose signal (the tool may not actually return that
  // entity's fields) but it is the cheapest useful proxy.
  const entityCount = inventory.totalEntities || 0;
  const entitiesCovered = inventory.coveredEntities || 0;
  const entityPct = entityCount > 0 ? Math.round((entitiesCovered / entityCount) * 100) : 0;

  const ageMs = inventory.lastScanAt > 0 ? Date.now() - inventory.lastScanAt : Infinity;
  const freshnessPct = computeFreshnessPct(ageMs);

  const awareness = awarenessScore({ endpointPct, modulePct: mods.pct, entityPct, freshnessPct });

  const lastScanLabel = inventory.lastScanAt
    ? formatRelative(ageMs)
    : 'never — run `npm run scan:inventory:dev`';

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-blue-50 to-rose-50 border border-emerald-200 rounded-xl p-5">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Awareness Score</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={`text-5xl font-bold ${awarenessColor(awareness)}`}>
              {awareness}<span className="text-xl text-gray-500">%</span>
            </span>
            <span className="text-sm text-gray-600 font-medium">
              weighted across endpoints · modules · freshness
            </span>
          </div>
          <p className="text-[11px] text-gray-600 mt-1">Last scan: {lastScanLabel}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <ScoreCell label="Endpoints" value={endpointPct} sub={`${covered}/${inventory.totalEndpoints}`} />
          <ScoreCell label="Modules" value={mods.pct} sub={`${mods.covered}/${mods.scorable}`} />
          <ScoreCell label="Entities" value={entityPct} sub={`${entitiesCovered}/${entityCount}`} />
          <ScoreCell label="Freshness" value={freshnessPct} sub={lastScanLabel} />
        </div>
      </div>

      {/* Secondary inventory counters */}
      <div className="mt-5 pt-4 border-t border-emerald-200/60 grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
        <StatCell label="Services" value={inventory.totalServices} />
        <StatCell label="Endpoints" value={inventory.totalEndpoints} />
        <StatCell label="Entities" value={entityCount} />
        <StatCell label="Tools" value={coverageTotals.total} />
        <StatCell label="Gaps" value={inventory.totalGaps} accent="rose" />
      </div>
    </div>
  );
};

export default AwarenessStrip;
